'use es6';

import I18n from 'I18n';
import { fromJS, List } from 'immutable';
import { renderToString } from 'react-dom/server';
import { formatPercent } from 'reporting-visualizations/formatters/propertyFormatter';
import identity from 'transmute/identity';
import { defaultOptions } from '../../../highcharts/options/defaultOptions';
import { getLegendPositionOptions } from '../../../highcharts/options/legendOptions';
import { SlicedChartSeries } from '../slicedRecords';
import { getTooltipFormatter } from './tooltips/legacyTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSeriesOptions = chart => {
  const {
    series = List()
  } = chart;
  return series.map(({
    key,
    name,
    points,
    dataLabelPointFormatter,
    events,
    innerSize
  }) => {
    return fromJS({
      __REF__: {
        key,
        points
      },
      name,
      data: points,
      dataLabels: {
        enabled: chart.getIn(['options', 'dataLabels']),
        formatter() {
          const {
            point
          } = this;
          const percent = formatPercent(point.percentage / 100);
          return `${percent} (${dataLabelPointFormatter(point.y)})`;
        }
      },
      innerSize,
      events: events || {},
      formatNumber: dataLabelPointFormatter
    });
  });
};
export const getTooltipOptions = (series, grandTotal) => ({
  formatter: getTooltipFormatter(series.get('dataLabelPointFormatter', identity), grandTotal)
});
const getTotalLabelContent = (totalName, totalCount) => {
  const TotalLabel = /*#__PURE__*/_jsxs("p", {
    children: [/*#__PURE__*/_jsx("b", {
      children: `${I18n.text('reporting-visualizations.labels.total')} ${totalName}:`
    }), ` ${totalCount}`]
  });
  return renderToString(TotalLabel);
};
const getTotalOnRender = (series, grandTotal) => {
  const seriesName = series.get('name');
  const dataLabelPointFormatter = series.get('dataLabelPointFormatter');
  return function () {
    const feTotal = this.series[0].total;
    const rawTotal = grandTotal == null ? feTotal : grandTotal;
    const formattedTotal = dataLabelPointFormatter ? dataLabelPointFormatter(rawTotal) : I18n.formatNumber(rawTotal, {
      precision: 2
    });
    const subtitle = getTotalLabelContent(seriesName, formattedTotal);
    this.setSubtitle({
      text: subtitle
    });
  };
};
export const buildChartOptions = chart => {
  const chartType = chart.get('options') ? {
    type: chart.getIn(['options', 'type'], 'pie').toLowerCase()
  } : {};
  const {
    legend: legendOptions = {},
    chart: legendOffsets = {}
  } = getLegendPositionOptions(chart.getIn(['options', 'legendPosition']), chart.getIn(['options', 'legendWidth']));
  const firstSeries = chart.get('series').first();
  const showTotals = chart.getIn(['options', 'showTotals']);
  const tooltipDisabled = chart.getIn(['options', 'tooltipDisabled']);
  const grandTotal = chart.getIn(['options', 'grandTotal']);
  const chartOptions = fromJS({
    chart: Object.assign({}, chartType, legendOffsets, {
      events: showTotals ? {
        render: getTotalOnRender(firstSeries || SlicedChartSeries, grandTotal)
      } : undefined
    }),
    plotOptions: {
      series: {
        states: chart.getIn(['options', 'disableSeriesHighlighting']) ? {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        } : {}
      }
    },
    title: {
      text: chart.getIn(['options', 'title'])
    },
    series: getSeriesOptions(chart),
    tooltip: tooltipDisabled ? {
      enabled: false
    } : getTooltipOptions(firstSeries || SlicedChartSeries, grandTotal),
    legend: Object.assign({}, legendOptions),
    accessibility: {
      point: {
        descriptionFormatter: point => {
          if (point.isNull || point.accessibilityLabel === undefined) {
            return false;
          }
          return point.accessibilityLabel;
        }
      }
    }
  });
  return defaultOptions.mergeDeep(chartOptions);
};